import { FC } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '../../accordion';
import { ArrowDownIcon, EditIcon, PlusIcon, SponsorsIcon } from '../../../icons';
import { Box, Typography } from '@mui/material';
import { createEventFlowStyles, textStyle } from './create-event-flow.styles';
import { flexColumn, flexRow } from '../../../styles';
import { LabeledSwitch } from '../../inputs/labeled-switch.component';
import { Colors } from '../../../constants';
import { EventPolicyTypesEnum, PrivacyTypesEnum } from '@sweatpals/shared';
import { useFormContext } from 'react-hook-form';
import { CreateEventFormData } from '../../../models/dto';
import { useCustomRegister } from '../../../hooks';
import { Button } from '../../buttons';
import { Showable } from '../../utils';
import { sub } from 'date-fns';
import { EventDateTimePicker } from '../event-date-time-picker';

export interface AdvancedSeettingsProps {
	isEditMode?: boolean;
	isEventIntegrationModal?: boolean;
	setIsQuestionnaireModalOpen(value: boolean): void;
	setIsWaiverModalOpen(value: boolean): void;
	setIsSponsorsModalOpen(value: boolean): void;
	setIsEventBundleModalOpen(value: boolean): void;
	setIsEventIntegrationModal?(value: boolean): void;
}

export const AdvancedSettings: FC<AdvancedSeettingsProps> = props => {
	const {
		isEditMode,
		isEventIntegrationModal,
		setIsQuestionnaireModalOpen,
		setIsWaiverModalOpen,
		setIsSponsorsModalOpen,
		setIsEventBundleModalOpen,
		setIsEventIntegrationModal,
	} = props;

	const form = useFormContext<CreateEventFormData>();

	const data = form.watch();

	const { register } = useCustomRegister(form);

	const eventFlowStyles = createEventFlowStyles();

	return (
		<Accordion>
			<AccordionSummary
				expandIcon={
					<ArrowDownIcon
						width={24}
						height={24}
					/>
				}
			>
				<Typography
					sx={textStyle({
						fontSize: '16px',
						fontWeight: '700',
					})}
				>
					Advanced Settings
				</Typography>
			</AccordionSummary>
			<AccordionDetails
				sx={{
					pt: '0',
				}}
			>
				<Box
					sx={flexColumn({
						gap: '18px',
					})}
				>
					<Box sx={eventFlowStyles.advancedSettingsCard.cardsRow}>
						<Box sx={eventFlowStyles.advancedSettingsCard.cardBox}>
							<Box component={SponsorsIcon} sx={eventFlowStyles.advancedSettingsCard.cardIcon} />

							<Box sx={eventFlowStyles.advancedSettingsCard.cardDescriptionBox}>
								<Typography sx={eventFlowStyles.advancedSettingsCard.cardDescriptionText}>
									Showcase brands that sponsor this event
								</Typography>

								<Box sx={eventFlowStyles.advancedSettingsCard.cardButtonsRow}>
									<Button
										strictColor
										sx={eventFlowStyles.advancedSettingsCard.cardButton}
										onClick={() => {
											setIsSponsorsModalOpen(true);
										}}
									>
										<Box component={PlusIcon} sx={eventFlowStyles.advancedSettingsCard.cardButtonIcon} />
										{data.sponsors?.length ? 'Edit' : 'Add'} sponsors
									</Button>
								</Box>
							</Box>
						</Box>
					</Box>

					<Box
						sx={flexColumn({
							gap: '18px',
						})}
					>
						<Box sx={flexColumn()}>
							<Box sx={eventFlowStyles.advancedSettingBox}>
								<LabeledSwitch
									{...register('withWaiver', {
										type: 'checkbox',
										valuePropKey: 'checked',
										onChange: e => {
											form.setValue('withWaiver', e.target.checked);
											setIsWaiverModalOpen(e.target.checked);
										},
									})}
									customLabel
									label={
										<Box
											sx={flexRow({
												alignItems: 'center',
												gap: '6px',
											})}
										>
											<Typography sx={eventFlowStyles.advancedSettingLabel}>Add a waiver</Typography>

											{data?.withWaiver ? (
												<Box
													component={EditIcon}
													sx={{
														color: Colors.white,
														width: '16px',
														height: '16px',
														cursor: 'pointer',
													}}
													onClick={() => {
														setIsWaiverModalOpen(true);
													}}
												/>
											) : null}
										</Box>
									}
									labelSx={eventFlowStyles.advancedSettingLabel}
								/>
								<Typography sx={eventFlowStyles.advancedSettingDescription}>
									Attendees sign a mandatory waiver when signing up.
								</Typography>
							</Box>
						</Box>

						<Box sx={eventFlowStyles.advancedSettingBox}>
							<LabeledSwitch
								{...register('withQuestionnaire', {
									type: 'checkbox',
									valuePropKey: 'checked',
									onChange: e => {
										form.setValue('withQuestionnaire', e.target.checked);

										if (e.target.checked) {
											setIsQuestionnaireModalOpen(true);
										} else {
											form.setValue('attendeeRegistration.fields', []);
										}
									},
								})}
								customLabel
								label={
									<Box
										sx={flexRow({
											alignItems: 'center',
											gap: '6px',
										})}
									>
										<Typography sx={eventFlowStyles.advancedSettingLabel}>Add attendee questionnaire</Typography>

										{data?.withQuestionnaire ? (
											<Box
												component={EditIcon}
												sx={{
													color: Colors.white,
													width: '16px',
													height: '16px',
													cursor: 'pointer',
												}}
												onClick={() => {
													setIsQuestionnaireModalOpen(true);
												}}
											/>
										) : null}
									</Box>
								}
							/>
							<Typography sx={eventFlowStyles.advancedSettingDescription}>
								Enter any additional information you&apos;d like attendees to provide.
							</Typography>
						</Box>

						<Box sx={eventFlowStyles.advancedSettingBox}>
							<LabeledSwitch
								name="showSectionsOnceForMultipleTickets"
								checked={data?.attendeeRegistration?.showSectionsOnceForMultipleTickets}
								onChange={(_e, value) => {
									form.setValue(
										'attendeeRegistration.showSectionsOnceForMultipleTickets',
										value,
										{ shouldDirty: true, shouldValidate: true }
									);
								}}
								label='Show attendee questionnaire once for multiple tickets'
								labelSx={eventFlowStyles.advancedSettingLabel}
							/>
							<Typography sx={eventFlowStyles.advancedSettingDescription}>
								Show attendee questionnaire once for purchaser.
								Information for other tickets will be collected on ticket transfer.
							</Typography>
						</Box>

						<Box sx={eventFlowStyles.advancedSettingBox}>
							<LabeledSwitch
								name="isWaitlistEnabled"
								checked={data?.isWaitlistEnabled}
								onChange={(_e, value) => {
									form.setValue(
										'isWaitlistEnabled',
										value,
										{ shouldDirty: true, shouldValidate: true }
									);
								}}
								label='Waitlists'
								labelSx={eventFlowStyles.advancedSettingLabel}
							/>
							<Typography sx={eventFlowStyles.advancedSettingDescription}>
								<Box
									component='span'
									sx={{
										fontWeight: '600',
										color: Colors.white,
									}}
								>
									Activate the waitlist for sold-out tiers
								</Box>
								<br />
								When a tier sells out, enable the waitlist for users to leave their contact details.
							</Typography>
						</Box>

						<Box sx={eventFlowStyles.advancedSettingBox}>
							<LabeledSwitch
								name="enableRefundPolicy"
								checked={data?.policyType === EventPolicyTypesEnum.AutoRefundUntil24hBeforeEvent}
								onChange={(_e, value) => {
									form.setValue(
										'policyType',
										value ? EventPolicyTypesEnum.AutoRefundUntil24hBeforeEvent : EventPolicyTypesEnum.NoRefunds,
										{ shouldDirty: true, shouldValidate: true }
									);
								}}
								label='Refund Policy'
								labelSx={eventFlowStyles.advancedSettingLabel}
							/>
							<Typography sx={eventFlowStyles.advancedSettingDescription}>
								{data?.policyType === EventPolicyTypesEnum.AutoRefundUntil24hBeforeEvent
									? 'Automatic full refund until 24h before event'
									: 'No Refund'}
							</Typography>
						</Box>

						<Box sx={flexColumn()}>
							<Box sx={eventFlowStyles.advancedSettingBox}>
								<LabeledSwitch
									{...register('withEventBundle', {
										type: 'checkbox',
										valuePropKey: 'checked',
										onChange: e => {
											form.setValue('withEventBundle', e.target.checked);
											setIsEventBundleModalOpen(e.target.checked);
										},
									})}
									customLabel
									label={
										<Box
											sx={flexRow({
												alignItems: 'center',
												gap: '6px',
											})}
										>
											<Typography sx={eventFlowStyles.advancedSettingLabel}>Add this event to Event Bundle</Typography>

											{data?.withEventBundle ? (
												<Box
													component={EditIcon}
													sx={{
														color: Colors.white,
														width: '16px',
														height: '16px',
														cursor: 'pointer',
													}}
													onClick={() => {
														setIsEventBundleModalOpen(true);
													}}
												/>
											) : null}
										</Box>
									}
									labelSx={eventFlowStyles.advancedSettingLabel}
								/>
								<Typography sx={eventFlowStyles.advancedSettingDescription}>
									Users who own Event Bundle can join for free
								</Typography>
							</Box>
						</Box>

						<LabeledSwitch
							{...register('withChatroom', {
								type: 'checkbox',
								valuePropKey: 'checked',
							})}
							label='Create an event chatroom'
							labelSx={eventFlowStyles.advancedSettingLabel}
						/>

						<Showable show={!isEditMode}>
							<Box sx={eventFlowStyles.advancedSettingBox}>
								<LabeledSwitch
									{...register('isCommunityChatSharingEnabled', {
										type: 'checkbox',
										valuePropKey: 'checked',
									})}
									disabled={data.privacyId === PrivacyTypesEnum.OnlyMe}
									label='Share in General Chat'
									labelSx={eventFlowStyles.advancedSettingLabel}
								/>
								<Typography sx={eventFlowStyles.advancedSettingDescription}>
									Notify everyone in my community through a groupchat message.
								</Typography>
							</Box>
						</Showable>

						<Box sx={eventFlowStyles.advancedSettingBox}>
							<LabeledSwitch
								{...register('isRsvpListHidden', {
									type: 'checkbox',
									valuePropKey: 'checked',
								})}
								label='Hide RSVP list'
								labelSx={eventFlowStyles.advancedSettingLabel}
							/>
							<Typography sx={eventFlowStyles.advancedSettingDescription}>
								Make the attendee list private. Only the event host will have access to view it.
							</Typography>
						</Box>

						<Box sx={eventFlowStyles.advancedSettingBox}>
							<LabeledSwitch
								{...register('withSalesStartDate', {
									type: 'checkbox',
									valuePropKey: 'checked',
								})}
								onChange={(_e, checked) => {
									form.setValue('withSalesStartDate', checked);

									if (!checked) {
										form.setValue('salesStartDate', undefined);
									}
								}}
								label='Control sales start date'
								labelSx={eventFlowStyles.advancedSettingLabel}
							/>
							<Typography sx={eventFlowStyles.advancedSettingDescription}>
								You can control when tickets go on sale. Or else the tickets will go on sale as soon as the event is
								published.
							</Typography>
							<Showable show={data.withSalesStartDate}>
								<Box mt='10px'>
									<EventDateTimePicker
										{...register('salesStartDate', { type: 'datetime-local' })}
										controlId="salesStartDate"
										minDate={sub(new Date(), { days: 1 })}
										maxDate={data.startDate ?? undefined}
										onChange={value => {
											form.setValue('salesStartDate', value ? new Date(value) : undefined);
										}}
										timezone={data.tzid}
										fullWidth
									/>
								</Box>
							</Showable>
						</Box>

						<Showable show={isEditMode}>
							<Box sx={eventFlowStyles.advancedSettingBox}>
								<LabeledSwitch
									name="tracking"
									onChange={(_e, checked) => {
										if (!checked) {
											form.setValue('additionalData.tracking', {
												attnId: '',
												fbPixelId: '',
												gtagId: '',
											});
										}

										setIsEventIntegrationModal?.(checked);
									}}
									checked={Object.values(data.additionalData?.tracking ?? {}).some(Boolean) || isEventIntegrationModal}
									label={
										<Box
											sx={flexRow({
												alignItems: 'center',
												gap: '6px',
											})}
										>
											<Typography sx={eventFlowStyles.advancedSettingLabel}>Analytics Integrations</Typography>

											{Object.values(data.additionalData?.tracking ?? {}).some(Boolean) ? (
												<Box
													component={EditIcon}
													sx={{
														color: Colors.white,
														width: '16px',
														height: '16px',
														cursor: 'pointer',
													}}
													onClick={() => {
														setIsEventIntegrationModal?.(true);
													}}
												/>
											) : null}
										</Box>
									}
									labelSx={eventFlowStyles.advancedSettingLabel}
								/>
								<Typography sx={eventFlowStyles.advancedSettingDescription}>
									Connect tracking tools like Meta Pixel, Google Tag, or Attentive to monitor event performance
								</Typography>
							</Box>
						</Showable>

						<Showable show={!isEditMode}>
							<Box sx={eventFlowStyles.advancedSettingBox}>
								<LabeledSwitch
									{...register('allowToPublishToOtherPlatforms', {
										type: 'checkbox',
										valuePropKey: 'checked',
									})}
									label='Cross‑post to Local Event Sites'
									labelSx={eventFlowStyles.advancedSettingLabel}
								/>

								<Typography sx={eventFlowStyles.advancedSettingDescription}>
									We’ll promote your events to popular event sites like Eventbrite and Meetup, driving more attendees directly to your SweatPals listing and increasing your sales, for free.
								</Typography>
							</Box>
						</Showable>
					</Box>
				</Box>
			</AccordionDetails>
		</Accordion>
	);
};
